import { Variable } from 'src/api/types';
import _ from 'lodash';

const toSnakeCaseName = (variable: Variable): string => {
  const displayName = variable.alias || variable.name;
  return _.snakeCase(displayName);
};

/**
 * Converts a formula string from ID format (#123) to snake_case variable names format.
 * Used for CSV export to make formulas human-readable and re-importable.
 *
 * @example
 * formulaIdsToNames("#500001 * 0.1", variables) => "#annual_revenue * 0.1"
 */
export const formulaIdsToNames = (formula: string | null | undefined, variables: Variable[]): string => {
  if (!formula) return '';

  let result = formula;
  const regex = /#(\d+)/g;
  let match;

  while ((match = regex.exec(formula)) !== null) {
    const id = Number(match[1]);
    const variable = variables.find((v) => v.id === id);
    if (variable) {
      const snakeName = toSnakeCaseName(variable);
      result = result.replace(match[0], `#${snakeName}`);
    }
    // If variable not found, keep original #ID format
  }

  return result;
};

export interface FormulaVariableInfo {
  name: string;
  snakeName: string;
  found: boolean;
  variableId?: number;
}

export interface FormulaNamesToIdsResult {
  formula: string;
  missingVariables: string[];
  foundVariables: FormulaVariableInfo[];
}

/**
 * Converts a formula string from snake_case variable names format to ID format (#123).
 * Used for CSV import to convert human-readable formulas to system format.
 *
 * @example
 * formulaNamesToIds("#annual_revenue * 0.1", variables) => { formula: "#500001 * 0.1", missingVariables: [], foundVariables: [...] }
 */
export const formulaNamesToIds = (
  formula: string | null | undefined,
  variables: Variable[],
): FormulaNamesToIdsResult => {
  if (!formula) return { formula: '', missingVariables: [], foundVariables: [] };

  const missingVariables: string[] = [];
  const foundVariables: FormulaVariableInfo[] = [];
  let result = formula;

  // Match #snake_case_name format (letters, numbers, underscores, not starting with digit)
  const snakeRegex = /#([a-z][a-z0-9_]*)/g;
  let match;

  while ((match = snakeRegex.exec(formula)) !== null) {
    const snakeName = match[1];
    const readableName = snakeName.replace(/_/g, ' ');

    const variable = findVariableBySnakeName(snakeName, variables);

    if (variable) {
      result = result.replace(match[0], `#${variable.id}`);
      foundVariables.push({
        name: readableName,
        snakeName,
        found: true,
        variableId: variable.id,
      });
    } else {
      missingVariables.push(readableName);
      foundVariables.push({
        name: readableName,
        snakeName,
        found: false,
      });
    }
  }

  // Deduplicate by snakeName
  const uniqueFoundVariables = foundVariables.filter(
    (v, i, arr) => arr.findIndex((x) => x.snakeName === v.snakeName) === i,
  );

  return {
    formula: result,
    missingVariables: [...new Set(missingVariables)],
    foundVariables: uniqueFoundVariables,
  };
};

const findVariableBySnakeName = (snakeName: string, variables: Variable[]): Variable | undefined => {
  return variables.find((v) => {
    const varSnakeName = toSnakeCaseName(v);
    return varSnakeName === snakeName;
  });
};

/**
 * Checks if a string is a pure numeric value or a formula expression.
 * Pure numbers can be used directly; formulas need variable resolution.
 */
export const isNumericValue = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  // Check if it's just a number (including decimals and negative)
  return /^-?\d+(\.\d+)?$/.test(trimmed);
};

/**
 * Parses a consequence value that could be either a number or a formula.
 * Returns the formula string (converting number to string if needed).
 */
export const parseConsequenceValue = (
  value: string | number | null | undefined,
  variables: Variable[],
): FormulaNamesToIdsResult => {
  if (value === null || value === undefined) {
    return { formula: '', missingVariables: [], foundVariables: [] };
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return { formula: '', missingVariables: [], foundVariables: [] };
  }

  if (isNumericValue(stringValue)) {
    return { formula: stringValue, missingVariables: [], foundVariables: [] };
  }

  return formulaNamesToIds(stringValue, variables);
};

export const extractVariableNames = (formula: string | null | undefined): string[] => {
  if (!formula) return [];

  const names: string[] = [];
  const snakeRegex = /#([a-z][a-z0-9_]*)/g;
  let match;

  while ((match = snakeRegex.exec(formula)) !== null) {
    const snakeName = match[1];
    // Convert snake_case to readable
    names.push(snakeName.replace(/_/g, ' '));
  }

  return [...new Set(names)];
};
